import { waitingForMessage } from './analysisGate';
import type { NamespaceList, NamespacePrefix, NamespaceUsageStatus } from './api';
import { describeApiError } from './apiErrors';

type FormatTime = (iso: string) => string;

function statementCount(n: number): string {
  return `${n.toLocaleString()} ${n === 1 ? 'statement' : 'statements'}`;
}

/** How a row reads its use by the data, from the kept prefix usage check;
 *  null for a built-in the data has not bound, which has no use to report. */
export function usageLabel(
  row: NamespacePrefix,
  list: NamespaceList,
  formatTime: FormatTime,
): string | null {
  if (row.builtin && !row.imported) return null;
  if (list.usage_checked_at === null || row.usage === null) return 'Not checked yet';
  if (!list.usage_current) return 'The data may have changed since the check';
  const use = row.usage === 0 ? 'Not used' : `Used in ${statementCount(row.usage)}`;
  return `${use} (checked ${formatTime(list.usage_checked_at)})`;
}

export function visibleRows(rows: NamespacePrefix[], unusedOnly: boolean): NamespacePrefix[] {
  return unusedOnly ? rows.filter((r) => r.usage === 0) : rows;
}

export function removableRows(rows: NamespacePrefix[]): NamespacePrefix[] {
  return rows.filter((r) => r.removable);
}

export function prefixCount(n: number): string {
  return `${n.toLocaleString()} ${n === 1 ? 'prefix' : 'prefixes'}`;
}

/** Where the prefix usage check stands, in words; null when it is neither
 *  running nor failed. */
export function usageCheckMessage(
  status: NamespaceUsageStatus,
  formatTime: FormatTime,
): string | null {
  if (status.running && status.waiting_for) {
    return `${waitingForMessage(status.waiting_for, formatTime)} The prefix usage check runs next.`;
  }
  if (status.running) {
    return `Counting the statements that use each prefix — ${status.running.started_by} started the check at ${formatTime(status.running.started_at)}. The counts fill in when it finishes.`;
  }
  if (status.last_failure) {
    const { error, detail } = status.last_failure;
    const why = error === 'the check did not finish' && !detail ? '' : ` ${describeApiError(500, error, detail)}`;
    return `The last prefix usage check did not finish.${why} Check again to count.`;
  }
  return null;
}

/** What a row that fails the add-prefix checks tells the reader to do about
 *  it, given what the last check found. */
export function invalidAdvice(row: NamespacePrefix, list: NamespaceList): string | null {
  if (row.invalid === null) return null;
  const head = `Not a valid prefix: ${row.invalid}.`;
  if (list.usage_checked_at === null || row.usage === null) {
    return `${head} Check which prefixes are used to see whether your data uses it.`;
  }
  if (row.usage === 0) {
    return list.usage_current
      ? `${head} No statement in your data uses it; Remove unused clears it.`
      : `${head} No statement used it at the last check; check again, then Remove unused clears it.`;
  }
  return (
    `${head} Statements in your data are written with it, so it stays while they are. ` +
    'Declaring a valid prefix for the same namespace does not rewrite those terms: ' +
    'the re-fold only moves terms written as full addresses. To clear it, remove the ' +
    'data that uses it with Delete source on the Graphs tab, check again and use Remove ' +
    'unused, then load the file again with a valid prefix.'
  );
}
